import React, { useEffect, useState, useCallback, memo } from 'react';
import { Text, View, FlatList, TouchableOpacity, Platform, ScrollView } from 'react-native';
import * as Animatable from 'react-native-animatable';
import { BlurView } from '@constants/Themed';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import AnimatedLottieView from '@utils/lottieAnimation';
import AnimatedTextFunction from '@hooks/animateTextEntry';
import { Pressable } from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import SwipeableRobot from '@components/RobotHelper/SwipeableRobot';
import { Label, SizableText, XStack, YStack, Card, H4, Spinner } from 'tamagui';
import SearchBar from '@navigation/screens/TopLevel/StoreScreen/Components/SearchBar';
import SearchComponent from '@components/Global/SearchComponent/SearchBar';
import { CountUp } from 'use-count-up';
import { useDispatch } from 'react-redux';
import { AntDesign, Entypo, FontAwesome5, MaterialIcons } from '@expo/vector-icons';
import { SHOW_UPDATE_MODAL } from '@reduxLocal/actions/types';
import { useAISuggestions } from './useAISuggestions';
import { AISuggestion, SuggestionCategory } from './AISuggestionsTypes';

// Category Configuration
const CATEGORY_CONFIG = {
  data: {
    icon: 'database',
    color: '#3b82f6',
    label: 'Data',
    description: 'Data analysis and management suggestions'
  },
  projects: {
    icon: 'folder-open',
    color: '#10b981',
    label: 'Projects',
    description: 'Project management and collaboration'
  },
  knowledge: {
    icon: 'lightbulb',
    color: '#f59e0b',
    label: 'Knowledge',
    description: 'Learning and knowledge management'
  },
  calendar: {
    icon: 'calendar-alt',
    color: '#ef4444',
    label: 'Calendar',
    description: 'Scheduling and time management'
  },
  templates: {
    icon: 'file-alt',
    color: '#8b5cf6',
    label: 'Templates',
    description: 'Document and workflow templates'
  }
} as const;

const PRIORITY_CONFIG = {
  high: { color: '#ef4444', label: 'High Priority' },
  medium: { color: '#f59e0b', label: 'Medium Priority' },
  low: { color: '#10b981', label: 'Low Priority' }
} as const;

// Suggestion Item Component
const SuggestionItem = memo(({
  suggestion,
  onPress,
  onDismiss,
  onMarkRead,
  theme
}: {
  suggestion: AISuggestion;
  onPress: (suggestion: AISuggestion) => void;
  onDismiss: (id: string) => void;
  onMarkRead: (id: string) => void;
  theme: boolean;
}) => {
  const categoryConfig = CATEGORY_CONFIG[suggestion.category];
  const priorityConfig = PRIORITY_CONFIG[suggestion.priority];

  const handlePress = useCallback(() => {
    onPress(suggestion);
    if (!suggestion.isRead) {
      onMarkRead(suggestion.id);
    }
  }, [suggestion, onPress, onMarkRead]);

  const handleDismiss = useCallback(() => {
    onDismiss(suggestion.id);
  }, [suggestion.id, onDismiss]);

  return (
    <Card
      style={{
        ...getModernCardStyle(theme, 'small'),
        marginVertical: 4,
        marginHorizontal: 0,
        opacity: suggestion.isDismissed ? 0.6 : 1,
        borderLeftWidth: 3,
        borderLeftColor: categoryConfig.color,
        minHeight: 80,
      }}
    >
      <TouchableOpacity onPress={handlePress} activeOpacity={0.7}>
        <YStack padding="$3" space="$2">
          {/* Header with category and priority */}
          <XStack justifyContent="space-between" alignItems="center">
            <XStack alignItems="center" space="$2">
              <View style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                backgroundColor: `${categoryConfig.color}20`,
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <FontAwesome5
                  name={categoryConfig.icon}
                  size={12}
                  color={categoryConfig.color}
                />
              </View>
              <YStack>
                <Text style={{
                  fontSize: 10,
                  fontWeight: '600',
                  color: categoryConfig.color,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  {categoryConfig.label}
                </Text>
                <View style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: priorityConfig.color,
                }} />
              </YStack>
            </XStack>

            <XStack alignItems="center" space="$2">
              {!suggestion.isRead && (
                <View style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#3b82f6',
                }} />
              )}
              <TouchableOpacity onPress={handleDismiss} style={{
                padding: 2,
                borderRadius: 8,
                backgroundColor: 'rgba(0,0,0,0.05)',
              }}>
                <MaterialIcons
                  name="close"
                  size={12}
                  color={getModernTextColor(theme, 'muted')}
                />
              </TouchableOpacity>
            </XStack>
          </XStack>

          {/* Title and description */}
          <YStack space="$1">
            <H4
              color={getModernTextColor(theme)}
              fontSize={14}
              fontWeight="600"
              numberOfLines={1}
            >
              {suggestion.title}
            </H4>
            <Text
              style={{
                fontSize: 12,
                color: getModernTextColor(theme, 'secondary'),
                lineHeight: 16,
              }}
              numberOfLines={2}
            >
              {suggestion.description}
            </Text>
          </YStack>

          {/* Metadata */}
          <XStack justifyContent="space-between" alignItems="center">
            <XStack alignItems="center" space="$2">
              <XStack alignItems="center" space="$1">
                <MaterialIcons
                  name="schedule"
                  size={10}
                  color={getModernTextColor(theme, 'muted')}
                />
                <Text style={{
                  fontSize: 10,
                  color: getModernTextColor(theme, 'muted'),
                }}>
                  {suggestion.metadata.estimatedTime}
                </Text>
              </XStack>

              <XStack alignItems="center" space="$1">
                <MaterialIcons
                  name="trending-up"
                  size={10}
                  color={getModernTextColor(theme, 'muted')}
                />
                <Text style={{
                  fontSize: 10,
                  color: getModernTextColor(theme, 'muted'),
                }}>
                  {suggestion.confidence}%
                </Text>
              </XStack>
            </XStack>

            <View style={{
              paddingHorizontal: 6,
              paddingVertical: 2,
              borderRadius: 8,
              backgroundColor: `${priorityConfig.color}20`,
            }}>
              <Text style={{
                fontSize: 8,
                fontWeight: '600',
                color: priorityConfig.color,
                textTransform: 'uppercase',
              }}>
                {priorityConfig.label}
              </Text>
            </View>
          </XStack>
        </YStack>
      </TouchableOpacity>
    </Card>
  );
});

// Category Filter Component
const CategoryFilter = memo(({
  selectedCategory,
  onCategoryChange,
  categoryStats,
  theme
}: {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  categoryStats: Record<SuggestionCategory, number>;
  theme: boolean;
}) => {
  const categories = Object.keys(CATEGORY_CONFIG) as Array<keyof typeof CATEGORY_CONFIG>;

  return (
    <Card style={{
      ...getModernCardStyle(theme, 'small'),
      marginHorizontal: 0,
      marginVertical: 0,
    }}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 8,
          paddingVertical: 6,
          paddingRight:"30%",
          gap: 8,
        }}
        style={{ flexGrow: 0 }}
      >
        <TouchableOpacity
          style={{
            ...getModernActionStyle(theme, selectedCategory === 'all' ? 'blue' : 'grey'),
            paddingHorizontal: 14,
            paddingVertical: 8,
            borderRadius: 18,
            minWidth: 80,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onPress={() => onCategoryChange('all')}
        >
          <Text numberOfLines={1} style={{
            color: theme ? "black" : 'white',
            fontSize: 12,
            fontWeight: '600',
            textAlign: 'center',
          }}>
            All ({Object.values(categoryStats).reduce((sum, count) => sum + count, 0)})
          </Text>
        </TouchableOpacity>

        {categories.map((category) => {
          const config = CATEGORY_CONFIG[category];
          const count = categoryStats[category];

          return (
            <TouchableOpacity
              key={category}
              style={{
                ...getModernActionStyle(theme, selectedCategory === category ? 'blue' : 'grey'),
                paddingHorizontal: 14,
                paddingVertical: 8,
                borderRadius: 18,
                minWidth: 120,
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onPress={() => onCategoryChange(category)}
            >
              <XStack alignItems="center" space="$2" justifyContent="center">
                <FontAwesome5
                  name={config.icon}
                  size={11}
                  color={theme ? "black" : 'white'}
                />
                <Text numberOfLines={1} style={{
                  color: theme ? "black" : 'white',
                  fontSize: 12,
                  fontWeight: '600',
                  textAlign: 'center',
                }}>
                  {config.label} ({count})
                </Text>
              </XStack>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </Card>
  );
});

const AIActions = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [keyForAnimatedText, setKeyForAnimatedText] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const theme = themeSelector();
  const dispatch = useDispatch();

  // Use AI Suggestions hook
  const {
    suggestions,
    isLoading,
    error,
    categoryStats,
    totalCount,
    refresh,
    dismissSuggestion,
    markAsRead,
    getUnreadCount,
    getHighPriorityCount
  } = useAISuggestions({
    autoLoad: true,
    refreshInterval: 5 * 60 * 1000, // Refresh every 5 minutes
  });

  // Filter suggestions by category
  const filteredSuggestions = selectedCategory === 'all'
    ? suggestions.filter(s => !s.isDismissed)
    : suggestions.filter(s => s.category === selectedCategory && !s.isDismissed);

  const textHelp = [
    'Here I will list any AI actions available for your data to help your project',
    "Slide away any actions that aren't helpful and I will adjust my suggestions accordingly",
    `I found ${filteredSuggestions.length} personalized suggestions for you`,
    `You have ${getUnreadCount()} new suggestions and ${getHighPriorityCount()} high priority items`
  ];

  useEffect(() => {
    // Update the key whenever currentIndex changes
    setKeyForAnimatedText((prevKey) => prevKey + 1);
  }, [currentIndex]);

  const getText = () => {
    return textHelp[currentIndex];
  };

  // Callback hooks
  const handleSuggestionPress = useCallback((suggestion: AISuggestion) => {
    console.log('Suggestion pressed:', suggestion);
    // TODO: Implement suggestion action
  }, []);

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
  }, []);

  const renderSuggestionItem = useCallback(({ item }: { item: AISuggestion }) => (
    <SuggestionItem
      suggestion={item}
      onPress={handleSuggestionPress}
      onDismiss={dismissSuggestion}
      onMarkRead={markAsRead}
      theme={theme}
    />
  ), [handleSuggestionPress, dismissSuggestion, markAsRead, theme]);

  const keyExtractor = useCallback((item: AISuggestion) => item.id, []);

  const EmptyStateComponent = useCallback(() => (
    <Card style={{
      ...getModernCardStyle(theme, 'small'),
      alignItems: 'center',
      justifyContent: 'center',
      marginHorizontal: 0,
      marginVertical: 4,
      padding: 16,
      minHeight: 100,
    }}>
      <YStack alignItems="center" space="$2">
        <MaterialIcons
          name="lightbulb-outline"
          size={24}
          color={getModernTextColor(theme, 'muted')}
        />
        <H4
          color={getModernTextColor(theme)}
          textAlign="center"
          fontSize={14}
          fontWeight="600"
        >
          No suggestions available
        </H4>
        <Text style={{
          color: getModernTextColor(theme, 'secondary'),
          textAlign: 'center',
          fontSize: 12,
        }}>
          {selectedCategory === 'all'
            ? 'Check back later for personalized AI suggestions'
            : `No ${selectedCategory} suggestions at the moment`
          }
        </Text>
      </YStack>
    </Card>
  ), [theme, selectedCategory]);

  if (isLoading) {
    return (
      <View style={{ flex: 1, alignSelf: 'stretch', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 0, borderRadius: 20 }}>
        <SearchComponent aiSearch  />
        <YStack style={{ marginTop: 16, maxWidth: "90%", flex: 1, alignSelf: "stretch", gap: 12, paddingLeft: "10%" }}>
          <Card style={{
            ...getModernCardStyle(theme, 'medium'),
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
            minHeight: 200,
          }}>
            <YStack alignItems="center" space="$4">
              <Spinner size="large" color="$blue10" />
              <H4 color="$color">Loading AI suggestions...</H4>
            </YStack>
          </Card>
        </YStack>
      </View>
    );
  }

  if (error) {
    return (
      <View style={{ flex: 1, alignSelf: 'stretch', width: '100%', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 0, borderRadius: 20 }}>
        <SearchComponent aiSearch extraComponents={(<View>

        </View>)} />
        <YStack style={{ marginTop: 16, flex: 1, alignSelf: "stretch", gap: 12 }}>
          <Card style={{
            ...getModernCardStyle(theme, 'medium'),
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
            minHeight: 200,
          }}>
            <YStack alignItems="center" space="$4">
              <MaterialIcons
                name="error-outline"
                size={48}
                color="#f44336"
              />
              <H4 color="#f44336" textAlign="center" fontSize={18} fontWeight="600">
                Failed to load suggestions
              </H4>
              <Text style={{
                color: getModernTextColor(theme, 'secondary'),
                textAlign: 'center',
                fontSize: 14,
              }}>
                {error}
              </Text>
              <TouchableOpacity
                style={{
                  ...getModernActionStyle(theme, 'blue'),
                  paddingHorizontal: 24,
                  paddingVertical: 12,
                  borderRadius: 12,
                }}
                onPress={refresh}
              >
                <Text style={{
                  color: 'white',
                  fontSize: 14,
                  fontWeight: '600',
                }}>
                  Retry
                </Text>
              </TouchableOpacity>
            </YStack>
          </Card>
        </YStack>

      </View>
    );
  }

  return (
    <View style={{ flex: 1, alignSelf: 'stretch', paddingTop: 40, paddingBottom: 0, borderRadius: 20 }}>
      <SearchComponent customStyle={{ left: "10%" }} aiSearch />
      <YStack style={{ marginTop: 16, maxWidth: "90%", flex: 1, alignSelf: "stretch", gap: 12, paddingLeft: "10%" }}>
        {/* AI Suggestions Header */}
        <Card style={{
          ...getModernCardStyle(theme, 'small'),
          paddingHorizontal: 16,
          paddingVertical: 12,
        }}>
          <XStack flexWrap="wrap" justifyContent="space-between" alignItems="center">
            <YStack>
              <Text style={{
                fontSize: 14,
                fontWeight: '700',
                color: getModernTextColor(theme),
                marginBottom: 2
              }}>
                AI Suggestions
              </Text>
              <Text style={{
                fontSize: 11,
                color: getModernTextColor(theme, 'secondary'),
                opacity: 0.8
              }}>
                Personalized recommendations based on your activity
              </Text>
            </YStack>

            <XStack alignItems="center" space="$2">
              {getUnreadCount() > 0 && (
                <View style={{
                  paddingHorizontal: 6,
                  paddingVertical: 3,
                  borderRadius: 10,
                  backgroundColor: '#3b82f6',
                }}>
                  <Text style={{
                    fontSize: 9,
                    fontWeight: '600',
                    color: 'white',
                  }}>
                    {getUnreadCount()} new
                  </Text>
                </View>
              )}

              {getHighPriorityCount() > 0 && (
                <View style={{
                  paddingHorizontal: 6,
                  paddingVertical: 3,
                  borderRadius: 10,
                  backgroundColor: '#ef4444',
                }}>
                  <Text style={{
                    fontSize: 9,
                    fontWeight: '600',
                    color: 'white',
                  }}>
                    {getHighPriorityCount()} high
                  </Text>
                </View>
              )}
            </XStack>
          </XStack>
        </Card>

        {/* Category Filter */}
        <CategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          categoryStats={categoryStats}
          theme={theme}
        />

        {/* Suggestions List */}
        <View style={[styles.defaultRoundedShadow, { flex: 1, marginHorizontal: "-5%" }]}>
          <FlatList
            data={filteredSuggestions}
            renderItem={renderSuggestionItem}
            keyExtractor={keyExtractor}
            showsVerticalScrollIndicator={false}
            style={{ flex: 1 }}
            contentContainerStyle={{
              paddingBottom: 120,
              gap: 20,
              paddingRight: 8,
            }}
            ListEmptyComponent={EmptyStateComponent}
            removeClippedSubviews={true}
            maxToRenderPerBatch={10}
            windowSize={5}
            initialNumToRender={10}
          />
        </View>
      </YStack>
    </View>
  );
};

export default AIActions;
